import React from 'react';

const AGG_LABELS = {
  SUM: 'Sum',
  COUNT: 'Count',
  COUNT_DISTINCT: 'Count Distinct',
  AVG: 'Average',
  MIN: 'Min',
  MAX: 'Max',
};

const AGG_FUNCTIONS = Object.keys(AGG_LABELS);

export default function PivotConfig({ columns, pivotRows, measures, onChangePivotRows, onChangeMeasures }) {
  const groupableColumns = columns.filter(c => c.groupable !== false);
  const aggregatableColumns = columns.filter(c => c.aggregatable || c.type === 'number');
  // COUNT and COUNT_DISTINCT can work on any column
  const allColumns = columns;

  const togglePivotRow = (key) => {
    if (pivotRows.includes(key)) {
      onChangePivotRows(pivotRows.filter(k => k !== key));
    } else if (pivotRows.length < 5) {
      onChangePivotRows([...pivotRows, key]);
    }
  };

  const updateMeasure = (idx, patch) => {
    const updated = [...measures];
    updated[idx] = { ...updated[idx], ...patch };
    onChangeMeasures(updated);
  };

  const removeMeasure = (idx) => {
    onChangeMeasures(measures.filter((_, i) => i !== idx));
  };

  const addMeasure = () => {
    onChangeMeasures([...measures, { function: 'SUM', column: '' }]);
  };

  // Columns available for a given aggregation function
  const getColumnsForFunction = (fn) => {
    if (fn === 'COUNT' || fn === 'COUNT_DISTINCT') return allColumns;
    return aggregatableColumns;
  };

  return (
    <div className="space-y-4">
      {/* Pivot Rows (Group By) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
            Rows (Group By)
            <span className="ml-1 text-gray-600 normal-case font-normal">up to 5</span>
          </h3>
          <span className="text-[10px] text-gray-600">{pivotRows.length}/5</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {groupableColumns.map(col => {
            const isSelected = pivotRows.includes(col.key);
            const order = isSelected ? pivotRows.indexOf(col.key) + 1 : null;
            return (
              <button
                key={col.key}
                onClick={() => togglePivotRow(col.key)}
                disabled={!isSelected && pivotRows.length >= 5}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                  isSelected
                    ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
              >
                {isSelected && (
                  <span className="bg-primary-600 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[9px]">
                    {order}
                  </span>
                )}
                {col.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Values (Measures) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Values (Measures)</h3>
          <button
            onClick={addMeasure}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            + Add
          </button>
        </div>

        {measures.length === 0 && (
          <p className="text-xs text-gray-600 italic">Add measures to aggregate data</p>
        )}

        <div className="space-y-1.5">
          {measures.map((m, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <select
                value={m.function}
                onChange={e => updateMeasure(idx, { function: e.target.value, column: m.column })}
                className="border border-gray-300 rounded text-xs px-1.5 py-1 bg-white w-28"
              >
                {AGG_FUNCTIONS.map(fn => (
                  <option key={fn} value={fn}>{AGG_LABELS[fn]}</option>
                ))}
              </select>

              <span className="text-xs text-gray-600">(</span>

              <select
                value={m.column}
                onChange={e => updateMeasure(idx, { column: e.target.value })}
                className="border border-gray-300 rounded text-xs px-1.5 py-1 bg-white flex-1 min-w-0"
              >
                <option value="">Select column...</option>
                {getColumnsForFunction(m.function).map(col => (
                  <option key={col.key} value={col.key}>{col.label}</option>
                ))}
              </select>

              <span className="text-xs text-gray-600">)</span>

              <button
                onClick={() => removeMeasure(idx)}
                className="text-gray-300 hover:text-red-500 p-0.5 flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
